import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Check } from "lucide-react";

// Case studies — add new ones to this array
const caseStudies = [
  {
    id: "fever-dream",
    title: "FEVER DREAM",
    subtitle: "Character-led scenic installation",
    meta: "TAPE London · Halloween 2025",
    strapline: "A surreal character. A fully fabricated DJ booth. A stage that became part of the nightmare.",
    heroImage: "/images/feverdream/result-booth.jpg",
    credit: "In partnership with Julia Charles Event Management",
    intro: [
      "For Halloween 2025, TAPE London became Fever Dream — an immersive world where nothing quite made sense. Oversized characters, distorted installations, vivid colour, theatrical lighting and odd little details filled every corner of the venue. The idea was never to decorate a nightclub for Halloween. It was to completely change how the space felt.",
      "The overall aesthetic of Fever Dream was curated in partnership with Julia Charles Event Management, who we worked alongside throughout the project to bring the world to life.",
      "At the centre of the stage sat a bespoke, character-led DJ booth — a familiar, playful character reimagined with a much darker personality. We were brought in to turn that idea into a physical build."
    ],
    sections: [
      {
        heading: "The Idea",
        paragraphs: [
          "The brief was to create a large-scale character DJ booth that could hold the Fever Dream stage on its own. It had to feel instantly recognisable, slightly unsettling, and completely at home in the chaotic world being built around it.",
          "The character wasn't just set dressing placed behind the decks. It was the DJ booth. So the design had to work as a practical performance space while still reading as one huge sculptural character from the dance floor."
        ]
      },
      {
        heading: "The Challenge",
        paragraphs: [
          "With a build this unusual, there's a lot more going on underneath the finished surface than most people realise. The character had to be big, structurally sound and genuinely usable, without losing the exaggerated proportions and organic shapes that make the design work.",
          "Along the way we had to fit in:"
        ],
        checklist: [
          "A substantial internal structure",
          "A working DJ position",
          "Large sculpted character elements",
          "Integrated illuminated eyes",
          "Detailed facial features",
          "Fur and textured finishes",
          "Branded graphic details",
          "A finish tough enough for a busy nightclub"
        ],
        after: "And like every live event build, it had to travel from our workshop to the venue and be ready for opening night."
      },
      {
        heading: "What We Did",
        paragraphs: [
          "We started with the character's shapes and proportions and broke the design down into parts we could actually fabricate.",
          "CNC-cut components came first, giving us the big sculptural forms with the accuracy needed to capture the character's distinctive shape. Those elements were then assembled around a structural framework, building the underlying form before the sculpted exterior took over.",
          "The face was built up and finished by hand — the features, teeth and ears all carefully formed and prepared for paint.",
          "The fur was where the transformation really happened. What started as a workshop-built structure slowly became the creature itself, with the fur worked across the surrounding forms and carefully trimmed in around the face and features.",
          "Paint, graphics, lighting and the finishing details brought everything together — including the illuminated eyes, which gave the character its final expression. The end result looked playful from one angle and properly unsettling from another. Exactly what Fever Dream called for."
        ]
      },
      {
        heading: "The Result",
        paragraphs: [
          "The finished character became one of the defining features of the Fever Dream stage. Rather than sitting behind the DJ, the booth became part of the performance itself — the stage turned into another piece of the story.",
          "The mix of oversized scale, sculptural fabrication, fur, lighting and character detail gave TAPE London something that couldn't be bought off the shelf. It was designed, fabricated and built specifically for the event.",
          "That's the part of this job we love most. Someone has an idea that doesn't exist yet. We figure out how to build it."
        ]
      }
    ],
    timelineHeading: "From Workshop To Stage",
    timeline: [
      { step: "01", label: "CNC & Form", caption: "Breaking the character down into precise, workable components.", image: "/images/feverdream/01-cnc.jpg" },
      { step: "02", label: "Structure", caption: "Building the internal framework and establishing the scale.", image: "/images/feverdream/02-structure.jpg" },
      { step: "03", label: "Building The Form", caption: "Assembling the sculptural elements around the framework.", image: "/images/feverdream/03-form.jpg" },
      { step: "04", label: "Sculpting", caption: "Developing the face, features and character forms.", image: "/images/feverdream/04-sculpt.jpg" },
      { step: "05", label: "Detailing", caption: "Adding the teeth, ears, eyes and surface details.", image: "/images/feverdream/05-detail.jpg" },
      { step: "06", label: "Fur & Finish", caption: "Transforming the fabricated structure into the finished character.", image: "/images/feverdream/06-fur.jpg" },
      { step: "07", label: "Lighting", caption: "Bringing the eyes — and the character — to life.", image: "/images/feverdream/07-lighting.jpg" },
      { step: "08", label: "TAPE London", caption: "The finished Fever Dream DJ booth in situ.", image: "/images/feverdream/08-insitu.jpg" }
    ],
    resultImages: [
      { src: "/images/feverdream/result-booth.jpg", caption: "The finished DJ booth on the Fever Dream stage" },
      { src: "/images/feverdream/result-eyes.jpg", caption: "The character coming to life on opening night" }
    ],
    extra: {
      heading: "Making An Entrance",
      paragraphs: [
        "The DJ booth wasn't the only character guests met that night. As part of the same project, we built a 4.2m version of the creature around the venue entrance — so the Fever Dream world started before anyone even stepped inside.",
        "Same character, same build approach: structural framework, sculpted forms, fur and finishing — scaled up and wrapped around a working doorway."
      ],
      images: [
        "/images/feverdream/door-fur-ears.jpg",
        "/images/feverdream/door-fur-teeth.jpg",
        "/images/feverdream/door-final-1.jpg",
        "/images/feverdream/door-final-2.jpg"
      ]
    },
    whatWeBuilt: [
      "Bespoke character DJ booth",
      "CNC fabrication",
      "Structural fabrication",
      "Sculpted scenic elements",
      "Fur application",
      "Paint & finishing",
      "Integrated lighting",
      "Branded detailing",
      "Event-ready installation"
    ]
  },
  {
    id: "the-boombox",
    title: "THE BOOMBOX",
    subtitle: "Bespoke Scenic DJ Booth · Mayfair",
    meta: "Music Event · Mayfair",
    strapline: "One build. Multiple identities. Designed to change with the brief.",
    heroImage: "/images/boombox/result-red.jpg",
    intro: [
      "A classic boombox, reimagined as a full-scale working DJ booth. Created for a music event in Mayfair, the brief was to produce something instantly recognisable, playful and bold — but with enough flexibility to be transformed for different events, brands and environments.",
      "The answer was a bespoke, full-scale boombox DJ booth, designed and fabricated by MT. LOOP."
    ],
    sections: [
      {
        heading: "The Idea",
        paragraphs: [
          "We wanted to take something familiar and make it event-sized. The proportions, speakers, controls and graphic details of a classic boombox were exaggerated into a large scenic structure that could become the focal point of a DJ setup.",
          "But the clever part wasn't just the shape. The build was designed to be redecorated. Rather than creating a one-off scenic piece that would only ever work for a single event, we built a versatile structure that can be completely transformed through colour, graphics, lighting and finishes.",
          "That means the same physical build can become something completely different from event to event."
        ]
      },
      {
        heading: "The Challenge",
        paragraphs: [
          "A DJ booth has to do more than look good. It needs to be practical, robust and capable of working in a live event environment — while still reading as a convincing piece of oversized scenic design.",
          "We needed to balance:"
        ],
        checklist: [
          "Large-scale visual impact",
          "A practical DJ working position",
          "Strong structural construction",
          "Integrated lighting",
          "Detailed speaker and control features",
          "Easy access for equipment",
          "A finish suitable for repeated event use",
          "The ability to completely change its appearance between events"
        ],
        after: "The final piece needed to look like a giant boombox — without behaving like a giant headache."
      },
      {
        heading: "What We Did",
        paragraphs: [
          "We designed and fabricated the boombox as a substantial scenic structure, building the exaggerated speaker shapes, control panels and surrounding details into a single cohesive DJ booth.",
          "The construction allowed the visual elements to be finished and lit as part of the overall design, creating the glowing outline and oversized speaker details that give the booth its distinctive presence.",
          "But we didn't stop at the first finish. The structure was deliberately developed as a reconfigurable scenic asset.",
          "We've since shown just how dramatically the same build can change depending on the brief — from a clean, icy Christmas installation with illuminated reindeer and snow, to bold branded colourways and completely different graphic treatments.",
          "Same structure. Completely different character."
        ]
      },
      {
        heading: "The Result",
        paragraphs: [
          "The finished boombox became more than a DJ booth. It became a piece of event scenery in its own right — something designed to draw attention, frame the DJ and give the event a strong visual identity.",
          "And because the underlying structure is reusable, it has a life beyond a single event. It can be recoloured, rebranded, relit, redecorated — completely reimagined.",
          "Whether the brief calls for a nostalgic music aesthetic, a Christmas installation, a corporate brand activation or something completely unexpected, the boombox can evolve with it."
        ]
      }
    ],
    timelineHeading: "From Workshop To Event",
    timeline: [
      { step: "01", label: "Framework", caption: "Building the structural timber framework that everything hangs off.", image: "/images/boombox/01-framework.jpg" },
      { step: "02", label: "Structure", caption: "Assembling the carcass and establishing the oversized proportions.", image: "/images/boombox/02-structure.jpg" },
      { step: "03", label: "CNC Components", caption: "Cutting the cassette deck, controls and panel details with precision.", image: "/images/boombox/03-cnc.jpg" },
      { step: "04", label: "Speaker Forms", caption: "Constructing the exaggerated speaker shapes.", image: "/images/boombox/04-speakers.jpg" },
      { step: "05", label: "Priming & Detailing", caption: "Preparing every dial, button and component for finish.", image: "/images/boombox/05-priming.jpg" },
      { step: "06", label: "The Body Takes Shape", caption: "The full-scale boombox coming together in the workshop.", image: "/images/boombox/06-carcass.jpg" },
      { step: "07", label: "Speaker Details", caption: "Finished speaker discs and dials ready for fitting.", image: "/images/boombox/07-discs.jpg" },
      { step: "08", label: "Paint", caption: "Colour going on across every panel and component.", image: "/images/boombox/08-paint.jpg" },
      { step: "09", label: "Lighting & Graphics", caption: "Testing the illuminated branded panels before fitting.", image: "/images/boombox/09-lighting.jpg" },
      { step: "10", label: "The Finished Booth", caption: "Painted, branded, lit and ready for the event.", image: "/images/boombox/06-finish.jpg" }
    ],
    resultImages: [
      { src: "/images/boombox/result-red.jpg", caption: "A bold branded colourway" },
      { src: "/images/boombox/result-blue.jpg", caption: "Same build, completely different identity" }
    ],
    extra: {
      heading: "One Build. Endless Looks.",
      paragraphs: [
        "The boombox's party trick is transformation. The same structure has already gone from bold branded colourways to a clean, icy Christmas installation — complete with illuminated reindeer and snow.",
        "It can be recoloured, rebranded, relit and redecorated for whatever the brief calls for. It's also available to hire — head to our Hire page to make it yours."
      ],
      images: ["/images/boombox/christmas.jpg"]
    },
    whatWeBuilt: [
      "Bespoke boombox DJ booth",
      "Structural timber framework",
      "CNC-cut components",
      "Oversized speaker & control details",
      "Integrated lighting",
      "Paint & graphic finishes",
      "Reconfigurable scenic asset",
      "Event-ready installation"
    ],
    closing: "ONE BUILD. INFINITE POSSIBILITIES."
  }
];

const Reveal = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const SectionHeading = ({ children }) => (
  <div className="flex items-center gap-4 mb-8">
    <div className="w-12 h-[2px] bg-[#007e7a]" />
    <h3 className="text-xs uppercase tracking-[0.3em] text-[#007e7a] font-semibold">{children}</h3>
  </div>
);

const CaseStudy = ({ study }) => (
  <article id={study.id} data-testid={`case-study-${study.id}`} className="scroll-mt-24">
    {/* Case study hero */}
    <div className="relative overflow-hidden bg-[#174c4f]">
      <img
        src={study.heroImage}
        alt={study.title}
        className="absolute inset-0 w-full h-full object-cover opacity-30"
      />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
        <Reveal>
          <p className="tech-label text-[#ffd6a5] mb-6">{study.meta}</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[0.95] mb-4" data-testid={`case-study-${study.id}-title`}>
            {study.title}
          </h2>
          <p className="text-base md:text-lg text-white/80 uppercase tracking-[0.15em] mb-8">{study.subtitle}</p>
          <p className="text-lg md:text-xl text-[#ffd6a5] max-w-3xl mx-auto leading-relaxed italic mb-6">{study.strapline}</p>
          {study.credit && (
            <p className="text-sm text-white/60 tracking-[0.1em] uppercase" data-testid={`case-study-${study.id}-credit`}>{study.credit}</p>
          )}
        </Reveal>
      </div>
    </div>

    {/* Intro */}
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-24">
      <Reveal>
        {study.intro.map((para, i) => (
          <p key={i} className="text-base md:text-lg text-[#174c4f] leading-relaxed mb-6">{para}</p>
        ))}
      </Reveal>
    </div>

    {/* Narrative sections */}
    <div className="max-w-3xl mx-auto px-6 md:px-12 space-y-16 pb-8">
      {study.sections.map((section) => (
        <Reveal key={section.heading}>
          <SectionHeading>{section.heading}</SectionHeading>
          {section.paragraphs.map((para, i) => (
            <p key={i} className="text-base md:text-lg text-[#789994] leading-relaxed mb-5">{para}</p>
          ))}
          {section.checklist && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 my-8">
              {section.checklist.map((itemText) => (
                <div key={itemText} className="flex items-start gap-2 text-sm md:text-base text-[#174c4f]">
                  <Check size={16} className="text-[#007e7a] flex-shrink-0 mt-1" />
                  {itemText}
                </div>
              ))}
            </div>
          )}
          {section.after && (
            <p className="text-base md:text-lg text-[#789994] leading-relaxed">{section.after}</p>
          )}
        </Reveal>
      ))}
    </div>

    {/* Final build gallery */}
    {study.resultImages && (
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-16 pb-4" data-testid={`case-study-${study.id}-final-gallery`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {study.resultImages.map((img) => (
            <Reveal key={img.src}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={img.src} alt={img.caption} className="w-full h-full object-cover" />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#789994] mt-4">{img.caption}</p>
            </Reveal>
          ))}
        </div>
      </div>
    )}

    {/* Timeline */}
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid={`case-study-${study.id}-timeline`}>
      <Reveal className="text-center mb-20">
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="w-8 h-[2px] bg-[#007e7a]" />
          <span className="tech-label">The Process</span>
          <div className="w-8 h-[2px] bg-[#007e7a]" />
        </div>
        <h3 className="text-base md:text-lg font-semibold text-[#174c4f] uppercase tracking-[0.2em]">{study.timelineHeading}</h3>
      </Reveal>

      <div className="space-y-20 md:space-y-28">
        {study.timeline.map((stage, index) => (
          <Reveal key={stage.step}>
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center ${index % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-full h-full border-2 border-[#007e7a]/20" />
                <div className="relative aspect-square overflow-hidden">
                  <img src={stage.image} alt={`${stage.step} — ${stage.label}`} className="w-full h-full object-cover" />
                </div>
              </div>
              <div>
                <span className="project-number text-5xl md:text-6xl block mb-4">{stage.step}</span>
                <h4 className="text-xl md:text-2xl font-bold text-[#174c4f] uppercase tracking-[0.08em] mb-3">{stage.label}</h4>
                <p className="text-base text-[#789994] leading-relaxed">{stage.caption}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>

    {/* Extra section (door surround) */}
    {study.extra && (
      <div className="max-w-5xl mx-auto px-6 md:px-12 pb-20 md:pb-28" data-testid={`case-study-${study.id}-extra`}>
        <Reveal>
          <SectionHeading>{study.extra.heading}</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            <div>
              {study.extra.paragraphs.map((para, i) => (
                <p key={i} className="text-base md:text-lg text-[#789994] leading-relaxed mb-5">{para}</p>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {study.extra.images.map((img, i) => (
                <div key={i} className="aspect-[3/4] overflow-hidden">
                  <img src={img} alt={study.extra.heading} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    )}

    {/* What we built */}
    <div className="max-w-3xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
      <Reveal>
        <div className="border-2 border-[#007e7a]/30 bg-white p-8 md:p-12" data-testid={`case-study-${study.id}-what-we-built`}>
          <h3 className="text-xs uppercase tracking-[0.3em] text-[#007e7a] font-semibold mb-8 text-center">What We Built</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
            {study.whatWeBuilt.map((itemText) => (
              <div key={itemText} className="flex items-start gap-2 text-sm text-[#174c4f]">
                <Check size={15} className="text-[#007e7a] flex-shrink-0 mt-0.5" />
                {itemText}
              </div>
            ))}
          </div>
        </div>
      </Reveal>
      {study.closing && (
        <Reveal className="text-center mt-14">
          <p className="text-xl md:text-2xl font-bold text-[#174c4f] tracking-[0.12em]" data-testid={`case-study-${study.id}-closing`}>
            {study.closing}
          </p>
        </Reveal>
      )}
    </div>
  </article>
);

export default function BehindTheBuildsPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [hash]);

  return (
    <div data-testid="behind-the-builds-page" className="pattern-bg">
      {/* Page header */}
      <section className="pt-40 pb-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-[2px] bg-[#007e7a]" />
              <span className="tech-label">Case Studies</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#174c4f] leading-[0.95] mb-8" data-testid="behind-the-builds-title">
              Behind
              <br />
              <span className="text-[#007e7a]">The Builds</span>
            </h1>
            <p className="text-base md:text-lg text-[#789994] max-w-2xl leading-relaxed">
              The finished piece is only half the story. Here's how our builds go from
              an idea that doesn't exist yet to standing in a venue on opening night.
            </p>
          </motion.div>
        </div>
      </section>

      {caseStudies.map((study) => (
        <CaseStudy key={study.id} study={study} />
      ))}

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 bg-[#174c4f]" data-testid="btb-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-base md:text-lg font-semibold text-[#ffd6a5] uppercase tracking-[0.2em] mb-4">
            Got An Idea That Doesn't Exist Yet?
          </h2>
          <p className="text-white/80 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            That's our favourite kind of brief. Tell us what you're imagining and we'll figure out how to build it.
          </p>
          <a
            href="mailto:LAURA@MTLOOP.CO.UK?subject=Project%20Enquiry"
            className="btn-primary inline-flex items-center justify-center gap-3"
            data-testid="btb-cta-email-btn"
          >
            <Mail size={18} /> LAURA@MTLOOP.CO.UK
          </a>
        </div>
      </section>
    </div>
  );
}
